import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import type { CSSProperties, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

interface UseMobileNavigationStateOptions {
  headerRef: RefObject<HTMLDivElement | null>;
  overlayRef: RefObject<HTMLDivElement | null>;
  expandedByDefault: boolean;
  productTypesExpandedByDefault: boolean;
  sectorsExpandedByDefault: boolean;
  isPinned: boolean;
  isInteractive: boolean;
  /**
   * The active nav href for the current route. `CategoryNavigation` persists
   * across client-side navigations between two routes that resolve to the
   * same component (e.g. one interior category page to another), so this
   * hook can't rely on remounting to close the drawer after a nav link is
   * clicked. Watching for this to change lets it close explicitly instead.
   */
  activeHref?: string;
}

function scrollHeaderToStickyPosition(
  headerRef: RefObject<HTMLDivElement | null>,
) {
  const header = headerRef.current;

  if (!header) {
    return;
  }

  const { top } = header.getBoundingClientRect();

  window.scrollTo({
    top: Math.max(0, Math.round(window.scrollY + top)),
    behavior: 'smooth',
  });
}

/**
 * Manages the mobile category-navigation drawer as a sticky-aware interaction
 * model instead of a simple open/closed toggle.
 *
 * On mobile, the navigation header can be expanded before it has reached its
 * sticky position. If the drawer were opened immediately in that state, the UI
 * would appear detached from its header and the body-scroll lock would engage
 * before the sticky shell was visually settled. This hook coordinates the
 * sequence so the header first scrolls into its sticky position and only then
 * expands the drawer.
 *
 * The hook owns four related concerns that are easier to understand together
 * than split across the component:
 *
 * - top-level expansion state, including the delayed "expand once pinned"
 *   branch used when a user opens the menu before the header is sticky
 * - nested disclosure state for the product-types and sectors sections
 * - body-scroll locking for the overlay while the interactive drawer is open
 * - the small presentational transform state used for the overlay's CSS-driven
 *   entry/exit motion
 *
 * The resulting API gives `MobileNavigation` a straightforward render contract:
 * booleans for current state, a few event handlers, and one computed style
 * object for the animated overlay content.
 */
export function useMobileNavigationState({
  headerRef,
  overlayRef,
  expandedByDefault,
  productTypesExpandedByDefault,
  sectorsExpandedByDefault,
  isPinned,
  isInteractive,
  activeHref,
}: UseMobileNavigationStateOptions) {
  const [isExpanded, setIsExpanded] = useState(expandedByDefault);
  const [pendingPinnedExpansion, setPendingPinnedExpansion] = useState(false);
  const previousActiveHrefRef = useRef(activeHref);

  useEffect(() => {
    if (previousActiveHrefRef.current === activeHref) {
      return;
    }

    previousActiveHrefRef.current = activeHref;
    setPendingPinnedExpansion(false);
    setIsExpanded(false);
  }, [activeHref]);
  const [productTypesExpanded, setProductTypesExpanded] = useState(
    productTypesExpandedByDefault,
  );
  const [sectorsExpanded, setSectorsExpanded] = useState(
    sectorsExpandedByDefault,
  );

  useEffect(() => {
    if (!pendingPinnedExpansion || !isPinned) {
      return;
    }

    setPendingPinnedExpansion(false);
    setIsExpanded(true);
  }, [isPinned, pendingPinnedExpansion]);

  useEffect(() => {
    const overlay = overlayRef.current;

    if (!isExpanded || !overlay || !isInteractive) {
      return;
    }

    disableBodyScroll(overlay);

    return () => {
      enableBodyScroll(overlay);
    };
  }, [isExpanded, isInteractive, overlayRef]);

  const overlayContentStyle: CSSProperties = {
    transform: isExpanded ? 'translateY(0px)' : 'translateY(-8px)',
    willChange: 'transform, opacity',
  };

  const toggleExpanded = () => {
    if (isExpanded || pendingPinnedExpansion) {
      setPendingPinnedExpansion(false);
      setIsExpanded(false);
      return;
    }

    if (isPinned) {
      setIsExpanded(true);
      return;
    }

    setPendingPinnedExpansion(true);
    scrollHeaderToStickyPosition(headerRef);
  };

  return {
    isExpanded,
    productTypesExpanded,
    sectorsExpanded,
    closeExpanded: () => {
      setPendingPinnedExpansion(false);
      setIsExpanded(false);
    },
    overlayContentStyle,
    toggleExpanded,
    toggleProductTypesExpanded: () =>
      setProductTypesExpanded((current) => !current),
    toggleSectorsExpanded: () => setSectorsExpanded((current) => !current),
  };
}
